import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/joy';
import { motion, useReducedMotion } from 'framer-motion';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, pointerWithin, closestCenter, useSensor, useSensors, useDraggable, useDroppable } from '@dnd-kit/core';
import DragIndicatorRounded from '@mui/icons-material/DragIndicatorRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';

export function flattenTree(nodes, parent = null, depth = 0) {
  return nodes.flatMap(node => [{ node, parent, depth }, ...flattenTree(node.children || [], node.id, depth + 1)]);
}

export function moveTree(tree, id, target, { reveal = true } = {}) {
  if (!target || id === target.id) return tree;
  const flat = flattenTree(tree), source = flat.find(row => row.node.id === id);
  if (!source) return tree;
  const descendants = new Set(flattenTree([source.node]).map(row => row.node.id));
  if (descendants.has(target.parent)) return tree;
  const remove = nodes => nodes.filter(n => n.id !== id).map(n => n.children ? { ...n, children: remove(n.children) } : n);
  const insert = nodes => {
    const container = [...nodes];
    if (target.parent == null) {
      const index = target.id ? container.findIndex(n => n.id === target.id) : -1;
      container.splice(index < 0 ? container.length : index + (target.after ? 1 : 0), 0, source.node);
      return container;
    }
    return container.map(n => n.id === target.parent ? { ...n, ...(reveal ? { open: true } : {}), children: insertAt(n.children || []) } : n.children ? { ...n, children: insert(n.children) } : n);
  };
  const insertAt = nodes => {
    const result = [...nodes], index = target.id ? result.findIndex(n => n.id === target.id) : -1;
    result.splice(index < 0 ? result.length : index + (target.after ? 1 : 0), 0, source.node);
    return result;
  };
  return insert(remove(tree));
}

export function visibleTreeRows(nodes, isExpanded, active = null, parent = null, depth = 0) {
  return nodes.flatMap(node => {
    const row = { node, parent, depth };
    if (node.id === active) return [row];
    if (node.type === 'folder' && isExpanded && !isExpanded(node)) {
      // A closed folder shows only the insertion shadow, never its whole archive.
      const ghost = (node.children || []).find(child => child.id === active);
      return ghost ? [row, { node: ghost, parent: node.id, depth: depth + 1 }] : [row];
    }
    return [row, ...visibleTreeRows(node.children || [], isExpanded, active, node.id, depth + 1)];
  });
}

function Row({ row, active, children, expanded, onToggle, hovering }) {
  const reduced = useReducedMotion();
  const id = row.node.id;
  const drag = useDraggable({ id });
  const drop = useDroppable({ id: 'target-' + id, data: row, disabled: active === id });
  return <motion.div layout="position" transition={{ duration: reduced ? 0 : .18 }} data-reorder-row={id}
    ref={drop.setNodeRef} style={{ paddingLeft: row.depth * 20, position: 'relative' }}>
    <Box ref={drag.setNodeRef} sx={{ display: 'flex', alignItems: 'center', gap: .8, minHeight: 42, my: .4, px: 1,
      borderRadius: 12, minWidth: 0, border: '1px solid', borderColor: active === id ? 'primary.300' : 'transparent',
      bgcolor: active === id ? 'primary.softBg' : 'background.surface',
      boxShadow: active === id ? 'inset 0 2px 9px rgba(80,50,120,.12)' : 'none', opacity: active === id ? .38 : 1 }}>
      <Box component="button" type="button" {...drag.listeners} {...drag.attributes} aria-label={`Переместить ${row.node.name || row.node.title || 'задачу'}`}
        sx={{ border: 0, background: 'transparent', color: 'text.tertiary', display: 'grid', placeItems: 'center', cursor: 'grab', touchAction: 'none', p: .4, flexShrink: 0 }}><DragIndicatorRounded sx={{ fontSize: 19 }}/></Box>
      {row.node.type === 'folder' && onToggle && <Box component="button" type="button" aria-expanded={expanded} aria-label={`${expanded ? 'Свернуть' : 'Раскрыть'} папку ${row.node.name}`} onClick={e => { e.stopPropagation(); onToggle(row.node.id, !expanded); }}
        sx={{ border: 0, bgcolor: 'transparent', color: 'text.tertiary', display: 'grid', placeItems: 'center', cursor: 'pointer', p: .2, flexShrink: 0 }}><ChevronRightRounded sx={{ fontSize: 19, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .18s' }}/></Box>}
      {children}
    </Box>
    {hovering && <Box data-folder-hover={id} sx={{ height: 3, position: 'absolute', bottom: 1, left: row.depth * 20 + 12, right: 12, borderRadius: 4, bgcolor: 'primary.softBg', overflow: 'hidden', pointerEvents: 'none', '&:after': { content: '""', display: 'block', height: '100%', bgcolor: 'primary.400', transformOrigin: 'left', animation: 'ld-folder-open 2.5s linear forwards' }, '@keyframes ld-folder-open': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } } }}/>} 
  </motion.div>;
}

function EndTarget({ label }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'tree-end', data: { end: true } });
  return <Box ref={setNodeRef} data-reorder-end sx={{ minHeight: 42, mt: 1, border: '1px dashed', borderColor: isOver ? 'primary.400' : 'divider', borderRadius: 12, display: 'grid', placeItems: 'center', bgcolor: isOver ? 'primary.softBg' : 'transparent' }}><Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{label}</Typography></Box>;
}

// The projected row itself is the shadow. Moving it reflows every sibling,
// including the last row. Only release commits the complete placement.
export default function ReorderTree({ tree, onChange, canContain = (parent) => parent.type === 'folder', canRoot = () => true, render, disabled = false, columns = false, renderHeader, isExpanded, onToggle }) {
  const [active, setActive] = useState(null), [target, setTarget] = useState(null);
  const targetRef = useRef(null), pointer = useRef(null), rootRef = useRef(null);
  const hoverTimer = useRef(null), hoverId = useRef(null), [hovering, setHovering] = useState(null), latest = useRef(null);
  latest.current = { tree, onToggle, isExpanded };
  useEffect(() => () => clearTimeout(hoverTimer.current), []);
  function clearHover() { clearTimeout(hoverTimer.current); hoverTimer.current = null; hoverId.current = null; setHovering(null); }
  function hoverFolder(node) {
    if (!node || !onToggle || !isExpanded || isExpanded(node)) { if (hoverId.current) clearHover(); return; }
    if (hoverId.current === node.id) return;
    clearHover(); hoverId.current = node.id; setHovering(node.id);
    hoverTimer.current = setTimeout(() => {
      const current = latest.current, folder = flattenTree(current.tree).find(row => row.node.id === node.id)?.node;
      if (hoverId.current === node.id && folder && !current.isExpanded(folder)) current.onToggle?.(folder.id, true);
      clearHover();
    }, 2500);
  }
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }));
  const projected = active && target ? moveTree(tree, active, target, { reveal: false }) : tree;
  const source = flattenTree(tree).find(row => row.node.id === active)?.node;
  const collision = args => {
    pointer.current = args.pointerCoordinates;
    const p = args.pointerCoordinates, area = rootRef.current?.getBoundingClientRect();
    if (p && area && (p.x < area.left || p.x > area.right || p.y < area.top || p.y > area.bottom)) return [];
    const hits = pointerWithin(args);
    return hits.length ? hits : closestCenter(args);
  };
  function move({ over }) {
    if (!over || !source) { if (hoverId.current) clearHover(); return; }
    const data = over.data.current;
    let next;
    if (data?.endParent) {
      next = { parent: data.endParent, id: null };
    } else if (data?.end) {
      const last = tree.at(-1);
      if (!canRoot(source) && (!last || !canContain(last, source))) return;
      next = { parent: canRoot(source) ? null : last.id, id: null };
    } else if (data?.node) {
      if (data.node.id === active) return;
      const point = pointer.current, r = over.rect;
      const inside = point && point.y > r.top + r.height * .24 && point.y < r.bottom - r.height * .24 && point.x > r.left + data.depth * 20 + 96 && canContain(data.node, source);
      hoverFolder(inside && data.node.type === 'folder' ? data.node : null);
      if (!inside && data.parent == null && !canRoot(source)) return;
      next = inside ? { parent: data.node.id, id: null } : { parent: data.parent, id: data.node.id, after: point ? point.y > r.top + r.height / 2 : false };
      const parent = flattenTree(tree).find(row => row.node.id === next.parent)?.node;
      if (parent && !canContain(parent, source)) return;
    } else { if (hoverId.current) clearHover(); return; }
    if (!data?.node && hoverId.current) clearHover();
    if (JSON.stringify(next) !== JSON.stringify(targetRef.current)) { targetRef.current = next; setTarget(next); }
  }
  function clear() { clearHover(); setActive(null); setTarget(null); targetRef.current = null; }
  return <DndContext sensors={sensors} collisionDetection={collision} onDragStart={({ active: a }) => { if (!disabled) setActive(a.id); }} onDragMove={move} onDragOver={move}
    onDragCancel={clear} onDragEnd={({ over }) => { const parent = targetRef.current?.parent, next = over && targetRef.current ? moveTree(tree, active, targetRef.current) : tree; clear(); if (next !== tree && !disabled) { onChange(next); if (parent && onToggle) onToggle(parent, true); } }}>
    <Box ref={rootRef} aria-label="Перестановка элементов" sx={{ pointerEvents: disabled ? 'none' : 'auto' }}>
      {columns ? <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>{projected.map(column => <Box key={column.id} sx={{ width: 270, minWidth: 270 }}>
        {renderHeader(column)}
        {flattenTree(column.children || [], column.id).map(row => <Row key={row.node.id} row={row} active={active}>{render(row.node, row.depth)}</Row>)}
        <ColumnEnd id={column.id}/>
      </Box>)}</Box> : <>{visibleTreeRows(projected, isExpanded, active).map(row => <Row key={row.node.id} row={row} active={active} expanded={isExpanded?.(row.node)} onToggle={onToggle} hovering={hovering === row.node.id}>{render(row.node, row.depth)}</Row>)}<EndTarget label={source && !canRoot(source) ? 'В конец последнего раздела' : 'В конец корневого списка'}/></>}
      <Typography level="body-xs" sx={{ p: 1, color: 'text.tertiary', lineHeight: 1.6 }}>{columns ? 'За ручку — между карточками или в другую колонку. Esc — отмена.' : onToggle ? 'Стрелка — свернуть папку. Удерживайте над папкой 2,5 с, чтобы раскрыть. Esc — отмена.' : 'За ручку — между строками. В середину со сдвигом вправо — внутрь. Esc — отмена.'}</Typography>
    </Box>
    <DragOverlay dropAnimation={{ duration: 180 }}>{source && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.3, bgcolor: 'background.surface', borderRadius: 12, border: '1px solid', borderColor: 'primary.300', boxShadow: 'lg', maxWidth: '85vw' }}><DragIndicatorRounded/>{render(source, 0)}</Box>}</DragOverlay>
  </DndContext>;
}

function ColumnEnd({ id }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'column-end-' + id, data: { endParent: id } });
  return <Box ref={setNodeRef} sx={{ minHeight: 70, mt: 1, borderRadius: 12, border: '1px dashed', borderColor: isOver ? 'primary.400' : 'divider', bgcolor: isOver ? 'primary.softBg' : 'transparent', display: 'grid', placeItems: 'center' }}><Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Перенести сюда</Typography></Box>;
}
